import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, BarChart3, BrainCircuit, Users, X, PlusCircle, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CreateOption = ({ icon: Icon, label, description, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full h-full flex items-start gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-all group text-left border border-transparent hover:border-gray-100/50 min-h-[110px]"
  >
    <div className={`w-12 h-12 rounded-xl ${color} text-white shadow-lg group-hover:scale-110 transition-all duration-300 shrink-0 flex items-center justify-center`}>
      <Icon size={20} />
    </div>
    <div className="flex-1 flex flex-col justify-start min-w-0">
      <h4 className="font-bold text-gray-900 group-hover:text-brand-indigo transition-colors text-sm leading-tight">{label}</h4>
      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-2 leading-tight opacity-80">{description}</p>
    </div>
  </button>
);

const CreateMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const handleCreate = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]"
          />
          
          {/* Menu */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed bottom-28 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 w-[calc(100%-32px)] md:w-[480px] bg-white rounded-3xl shadow-2xl border border-gray-100 z-[90] overflow-hidden max-h-[70vh] md:max-h-[90vh] flex flex-col"
          >
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/30 shrink-0">
              <div>
                <h3 className="font-bold text-gray-400 uppercase tracking-widest text-[10px]">Quick Create</h3>
                <p className="text-[10px] text-gray-400 font-bold mt-1">Choose an activity to get started</p>
              </div>
              <button onClick={onClose} className="p-2.5 hover:bg-white rounded-2xl transition-all shadow-sm border border-transparent hover:border-gray-100">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent">
              <div className="px-4 py-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                <CreateOption 
                  icon={Radio} 
                  label="Live Q&A" 
                  description="Host interactive AMA"
                  color="bg-purple-500"
                  onClick={() => handleCreate('/rm/lv/new')}
                />
                <CreateOption 
                  icon={BrainCircuit} 
                  label="Ideation Hub" 
                  description="Collaborate on ideas"
                  color="bg-green-600"
                  onClick={() => handleCreate('/rm/bs/new')}
                />
                <CreateOption 
                  icon={BarChart3} 
                  label="Interactive Poll" 
                  description="Gather quick feedback"
                  color="bg-brand-indigo"
                  onClick={() => handleCreate('/rm/pl/new')}
                />
                <CreateOption 
                  icon={Shield} 
                  label="Mixed Session" 
                  description="All features enabled"
                  color="bg-brand-accent"
                  onClick={() => handleCreate('/rm/new?type=mixed')}
                />
              </div>
              
              <div className="px-6 py-6 bg-gray-50/50 flex flex-col gap-3">
                <div className="h-px bg-gray-100 w-full mb-1"></div>
                <button 
                  type="button"
                  onClick={() => handleCreate('/c/new')}
                  className="flex items-center justify-between p-4 min-h-[88px] bg-brand-indigo rounded-3xl text-white group hover:bg-brand-indigo/90 transition-all shadow-lg shadow-brand-indigo/20"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl group-hover:scale-110 transition-transform flex items-center justify-center">
                      <Users size={20} />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="font-bold text-sm leading-tight">Create Community</p>
                      <p className="text-[10px] text-white/70 font-bold uppercase tracking-widest mt-2 leading-tight">Build a persistent hub</p>
                    </div>
                  </div>
                  <div className="mr-2 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <PlusCircle size={18} />
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateMenu;
